/* eslint-disable @typescript-eslint/no-explicit-any */
import { Autocomplete, Spinner } from "src/components";

import { Check, Copy } from "lucide-react";
import moment from "moment";
import { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { counterKeysArray, divisionKeysArray, einvoiceKeysArray, employeeKeysArray, invoiceKeysArray, orderKeysArray, productKeysArray } from "src/constants/versioning"; //M10-11909
import { RootState } from "src/context/store";
import { versioningActions } from "src/context/versioning-slice";
import { useToast } from "src/hooks/useToast";
import {
  getCounterVersioning,
  getDivisionVersioning,
  getEinvoiceVersioning,  //M10-11909
  getEmployeeVersioning,
  getGeographyVersioning,
  getInvoiceVersioning,
  getOrderVersioning,
  getProductVersioning,
  getStockistVersioning,
} from "src/services/events-api";
import Dot from "../../assets/dot.png";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover.components";

interface ReplaceProductProps {
  // dataSource: Array<any>;
  type: string;
  invoiceId?: number;
  orderId?: number;
  stockistInvAttachmentId?: number;
}

interface DataProps {
  message: string;
  time: string;
  eventId: number | string;
  streamId: number | string;
  reference_number: string | null | undefined;
  created_by: {
    name: string;
    role: string;
    code: string;
  };
  event_type: any;
}

const VersioningCrate: React.FC<ReplaceProductProps> = ({ type, invoiceId, orderId, stockistInvAttachmentId }: ReplaceProductProps) => {
  const { toast } = useToast();
  const dispatch = useDispatch();
  const { productVersioning } = useSelector((state: RootState) => state.versioning);
  const { selectedData } = useSelector((state: RootState) => state.master);
  const primary_key = useSelector((state: RootState) => state.geography.primary_key);
  const { selectedCounterData } = useSelector((state: RootState) => state.counter);
  const { selectedData: selectedDivisionData } = useSelector((state: RootState) => state.division);
  const { selectedEmployeeData } = useSelector((state: RootState) => state.employee);
  const { selectedStockistData } = useSelector((state: RootState) => state.stockist);
  //M10-11526
  const filters = useSelector((state: RootState) => state.invoice.EInvoiceFilters); // M10-13251
  const einv_company_code = Array.isArray(filters?.company) && filters.company.some((item: any) => item?.code === 618) ? 618 : 0;
  //M10-11526
  //M10-13338
  const filtersOrder = useSelector((state: RootState) => state.ord.OrderFilters);
  const company_code = Array.isArray(filtersOrder?.company) && filtersOrder.company.some((item: any) => item?.code === 618) ? 618 : 0;
  //M10-13338
  // const { invoiceDetails, invoiceApprovalData } = useSelector((state: RootState) => state.invoice);

  // const [selectedFilter, setSelectedFilter] = useState<any>({});
  // const [selectedCase, setselectedCase] = useState<any>({});
  const [isFetching, setIsFetching] = useState(false);
  // The event log is served one page at a time and the backend's page size is 100. Nothing used
  // to request page 2, so any change that produces a lot of events at once pushed the record's
  // entire earlier history off the only page that was ever fetched — it read as "the history got
  // wiped". A counter merge now emits one event per product, so that is no longer a corner case.
  const VERSIONING_PAGE_SIZE = 100;
  const [page, setPage] = useState(0);
  const [hasMorePages, setHasMorePages] = useState(false);
  // Kept so "load older" continues under the filter the user picked rather than resetting to all.
  const [activeFilter, setActiveFilter] = useState<Record<string, unknown> | undefined>(undefined);

  let keysArray: any = [];
  switch (type) {
    case "Product":
      keysArray = productKeysArray;
      break;
    case "Employee":
      keysArray = employeeKeysArray;
      break;
    case "Counter":
      keysArray = counterKeysArray;
      break;
    case "Division":
      keysArray = divisionKeysArray;
      break;
    case "Geography":
      break;
    case "Order":
      keysArray = orderKeysArray;
      break;
    case "Invoice":
      keysArray = invoiceKeysArray;
      break;
    //M10-11909
    case "EInvoice":
      keysArray = einvoiceKeysArray;
      break;
    //M10-11909
    default:
      break;
  }

  // requestedPage 0 sends no page param at all, so the first load is byte-identical to before.
  const fetchVersioningData = async (data?: Record<string, unknown>, requestedPage = 0) => {
    try {
      setIsFetching(true);
      let res;
      const pageParam = requestedPage > 0 ? requestedPage : undefined;
      switch (type) {
        case "Product":
          res = await getProductVersioning(selectedData?.company_productcode, data?.value, data?.sendIn, pageParam);
          break;
        case "Employee":
          res = await getEmployeeVersioning(selectedEmployeeData?.empmaster_id, data?.value, data?.sendIn, pageParam); // Need to change it to employee specific code
          break;
        case "Counter":
          res = await getCounterVersioning(selectedCounterData?.counter_id, data?.value, data?.sendIn, pageParam); //Need to change it to counter specific code
          break;
        case "Division":
          res = await getDivisionVersioning(selectedDivisionData?.company_divisioncode, data?.value, data?.sendIn, pageParam); //Need to change it to counter specific code
          break;
        case "Geography":
          res = await getGeographyVersioning(primary_key);
          break;
        case "Stockist":
          res = await getStockistVersioning(selectedStockistData?.stockist_id);
          break;
        case "Invoice":
          res = await getInvoiceVersioning(invoiceId, stockistInvAttachmentId, data?.value, data?.sendIn);
          break;
        case "Order":
          res = await getOrderVersioning(orderId, data?.value, data?.sendIn, company_code);
          break;
        //M10-11909
        case "EInvoice":
          res = await getEinvoiceVersioning(stockistInvAttachmentId, einv_company_code);
          break;
        //M10-11909
        default:
          console.error("Invalid type");
          res = { status: 400 };
          break;
      }
      if (res?.status === 200) {
        setIsFetching(false);
        console.log("res", res);
        const fetchedLogs = res.data.logs || [];
        dispatch(
          versioningActions.setProductVersioning(requestedPage > 0 ? [...productVersioning, ...fetchedLogs] : fetchedLogs)
        );
        setPage(requestedPage);
        // A full page means there is probably another one; a short page means we have reached the
        // end. The backend does not report a total, so this is the only signal available.
        setHasMorePages(fetchedLogs.length >= VERSIONING_PAGE_SIZE);
        // Only announce the first load — "load older" would otherwise toast on every click.
        if (requestedPage === 0) {
          toast({
            title: "Success",
            description: `${type} versioning data fetched successfully`,
          });
        }
      } else {
        throw new Error();
      }
    } catch (error) {
      setIsFetching(false);

      console.error("err", error);
      toast({
        title: "Error",
        description: `Failed to fetch ${type} versioning data`,
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    fetchVersioningData();
  }, []);

  const VersionCard = ({ data }: { data: DataProps }) => {
    const [isCopied, setIsCopied] = useState(false);

    if (!data.message || !data.time || !data.created_by) {
      return null;
    }
    const { message, time, created_by, eventId, streamId, reference_number, event_type } = data;
    const { name, role } = created_by || {};
    const formattedTime = moment(time).format("h:mm A");

    const handleCopyReference = async () => {
      if (!reference_number) return;
      try {
        await navigator.clipboard.writeText(reference_number);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 1500);
      } catch (error) {
        console.error("Failed to copy reference number", error);
      }
    };

    return (
      <div>
        <div className="items-center justify-center overflow-y-scroll align-middle ">
          <div className=" border-b-[1px] border-b-[#999999] mt-5 relative" />
          <p className="text-sm text-center text-[#999999] bg-white mt-[-10px] z-auto absolute self-center left-[20%] right-[20%]">
            {moment(time).format("DD MMMM YYYY")} {formattedTime}
          </p>
          {/* <p className="flex text-[12px] mt-6 align-middle items-center text-[#49484C]">
            {name} <img src={Dot} alt="Dot" className="w-4 h-4" /> {code} <img src={Dot} alt="Dot" className="w-4 h-4" /> {role}
            <img src={Dot} alt="Dot" className="w-4 h-4" /> {eventId ? `event ${eventId}` : `event -`} <img src={Dot} alt="Dot" className="w-4 h-4" />{" "}
            {streamId ? `stream ${streamId}` : `stream -`}
          </p> */}
          <p className="flex text-[14px] mt-6 align-middle items-center text-[#49484C] gap-2">
            <p className="font-bold">{name}</p>
            <span className=" border  px-2 rounded-md text-[12px] text-[#707072]">{role}</span>
          </p>

          <p className="flex flex-col text-[14px] mt-2 align-middle  text-[#49484C] gap-1 border  p-2 rounded-md">
            <p className="flex text-[12px] align-middle items-center text-[#707072]">
              {eventId ? `${eventId}` : `-`}
              <img src={Dot} alt="Dot" className="w-4 h-4" />
              <span>{event_type}</span>
              <img src={Dot} alt="Dot" className="w-4 h-4" /> {streamId ? ` ${streamId}` : `-`}
            </p>
            <p className="font-semibold">{message}</p>
          </p>
          <div className="flex items-center justify-between mt-2 align-middle">
            <p className="text-xs">{/* {formattedTime} */}</p>
            <p className=" text-sm text-[#586AF5] cursor-pointer">
              <Popover>
                <PopoverTrigger>View Reference</PopoverTrigger>
                <PopoverContent className="bg-white flex items-start justify-between gap-2">
                  <span className="break-all">{reference_number ? reference_number : "No reference number"}</span>
                  {reference_number && (
                    <button
                      type="button"
                      onClick={handleCopyReference}
                      title="Copy reference number"
                      className="shrink-0 text-[#707072] hover:text-[#586AF5]"
                    >
                      {isCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </button>
                  )}
                </PopoverContent>
              </Popover>
            </p>
            {/* dummy commit */}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Fragment>
      <div
        id="scrollArea"
        className="p-6 w-[26rem] absolute max-h-[99.9%] h-[99.9%] bg-white right-0 -mt-6 z-50  rounded-md shadow-2xl overflow-y-scroll "
      >
        <Autocomplete
          onSelectionChange={(data: any) => {
            setActiveFilter(data);
            fetchVersioningData(data, 0);
          }}
          inputClassName="w-full h-auto"
          placeholder="Filter"
          suggestionList={keysArray}
          displayKey="label"
          uniqueKey="id"
        />
        {isFetching && (
          <div className="flex justify-center my-5">
            <Spinner size="medium" />
          </div>
        )}
        {[...productVersioning].map((data, index) => (
          <VersionCard key={index} data={data} />
        ))}
        {!isFetching && hasMorePages && (
          <button
            type="button"
            className="w-full mt-4 py-2 text-sm text-[#586AF5] border rounded-md"
            onClick={() => fetchVersioningData(activeFilter, page + 1)}
          >
            Load older entries
          </button>
        )}
      </div>

      {/* <Modal backdropClose={false} isOpen={open} onCloseModal={setOpen} isHeader={false}>*/}
      {/*  <div className="p-6 text-center w-[15rem] shadow-md flex flex-wrap">{referenceNumber}</div>*/}
      {/*</Modal>*/}
    </Fragment>
  );
};

export default VersioningCrate;
